/**
 * Where a picture's bytes come from — round 8 task 4, and round 8.1 task 4.
 *
 * ── The field bug ────────────────────────────────────────────────────────────
 * After a submit, and after an office recall, the cards rendered and every
 * picture was the `▣` glyph. Production D1 said the documents were intact —
 * each PhotoRef still `uploaded`, each with its `r2Key` — so only the LOCAL
 * bytes were gone: a recall replaces the survey on the phone and drops its
 * blobs, and iOS evicts them on its own under storage pressure. R2 is the
 * system of record and nothing on the phone could read it.
 *
 * ── The rule for the original ────────────────────────────────────────────────
 * Local first, always. The office is asked only for a picture it already
 * holds — `uploaded`, with an `r2Key` — because for anything else the phone
 * IS the only copy and a fetch could only fail. A picture that comes back is
 * written to the local row BEFORE it is returned, so the next render is local
 * again and the office is asked exactly once per lost picture. A miss and a
 * failure to ask both resolve to null: the tile shows what a lost picture
 * always showed, and nothing throws out of a render.
 *
 * ── The rule for the thumbnail (round 8.1, task 4) ───────────────────────────
 * Hyde's nine photos are 12.7 MB and the shelf drew every one full-size on a
 * 168×208 card. The shelf, the per-question tile and the cut sheet now draw
 * the THUMBNAIL (src/core/photoThumb.ts: 640 px on the longest side), and
 * only the viewer asks for the original. The order is by cost: the phone's
 * own thumb row; the office's thumb, a few tens of kilobytes; a thumb MADE
 * from the local original, which is a decode of the whole picture; and last
 * the office's original, a full download and a decode. Whichever way the
 * thumb arrives it is kept, so the next render finds it in the first place
 * it looks — and an original fetched for the purpose is kept too, so the
 * viewer never fetches it a second time. A thumb that cannot be made (a
 * picture this browser cannot decode) falls back to the original itself:
 * the card shows the picture, at whatever cost, rather than a glyph.
 *
 * Pure: the sources are handed in, so both rules are proved with fakes
 * (tests/unit/photo-bytes.test.ts) and the hooks in PhotoCapture.tsx only
 * wire `store.getPhotoBlob`, `api.fetchPhoto`, `makeThumbnail` and their
 * companions.
 */

import { THUMB_CONTENT_TYPE } from '../../core/photoThumb';
import type { PhotoRef } from '../../core/types';

export interface PhotoByteSources {
  /** The phone's own row, or undefined when it is gone. */
  getLocal: (photoId: string) => Promise<Blob | undefined>;
  /** The office's copy: null for "no such picture", a throw for "could not ask". */
  fetchRemote: (surveyId: string, photoId: string) => Promise<Blob | null>;
  /** Keep a picture that came back, so it is fetched once. */
  putLocal: (photoId: string, surveyId: string, blob: Blob) => Promise<unknown>;
  /** The phone's own thumbnail row, or undefined when it has none. */
  getThumb: (photoId: string) => Promise<ArrayBuffer | undefined>;
  /** Keep a thumbnail that was fetched or made late, so that happens once. */
  putThumb: (photoId: string, surveyId: string, bytes: ArrayBuffer) => Promise<unknown>;
  /** The office's small copy: null when it holds the photo but no thumb, a throw for "could not ask". */
  fetchRemoteThumb: (surveyId: string, photoId: string) => Promise<Blob | null>;
  /** A thumbnail made from the original's bytes; null when this browser cannot make one. */
  makeThumb: (original: Blob) => Promise<ArrayBuffer | null>;
}

/** True when the office can be expected to hold this photo's bytes. */
export function isHeldByOffice(ref: Pick<PhotoRef, 'uploadState' | 'r2Key'>): boolean {
  return ref.uploadState === 'uploaded' && ref.r2Key !== '';
}

/**
 * One question to the office, answered null when it could not be asked —
 * offline, or refused. The caller shows what it has and the next render asks
 * again once there is signal.
 */
async function ask(fetch: () => Promise<Blob | null>): Promise<Blob | null> {
  try {
    return await fetch();
  } catch {
    return null;
  }
}

/** Keep a picture, best effort: the phone may refuse (quota, most likely) and the picture still shows this once. */
async function keep(write: () => Promise<unknown>): Promise<void> {
  try {
    await write();
  } catch {
    // The next render fetches or makes it again rather than losing it.
  }
}

/**
 * One read of the phone's own tables, answered undefined when it could not
 * be made — a Dexie rejection (the database closing under a navigation, a
 * version change in another tab) used to escape `resolveThumbsAsTheyLand`
 * as an unhandled rejection with nothing on screen to show for it (final
 * review's fix wave, D-040 postscript). Guarded like `ask` guards the office:
 * a read that fails is a miss, and the next source is tried.
 */
async function read<T>(get: () => Promise<T | undefined>): Promise<T | undefined> {
  try {
    return await get();
  } catch {
    return undefined;
  }
}

export async function resolveOriginalBlob(
  sources: PhotoByteSources,
  ref: PhotoRef,
): Promise<Blob | null> {
  const local = await read(() => sources.getLocal(ref.id));
  if (local) return local;
  if (!isHeldByOffice(ref)) return null;

  const remote = await ask(() => sources.fetchRemote(ref.surveyId, ref.id));
  // An empty body is a miss, not a picture: `putLocal` refuses an empty read
  // (D-040 §2) and returning it anyway drew nothing while claiming a hit.
  if (!remote || remote.size === 0) return null;
  await keep(() => sources.putLocal(ref.id, ref.surveyId, remote));
  return remote;
}

/** A thumbnail's bytes as the Blob an object URL wants; every thumb is a JPEG. */
function thumbBlobOf(bytes: ArrayBuffer): Blob {
  return new Blob([bytes], { type: THUMB_CONTENT_TYPE });
}

/**
 * The thumb made from an original, kept, and returned — or the original
 * itself when this browser cannot make one, so the card still shows the
 * picture. Nothing is kept in that case: there is nothing to keep, and the
 * original is already wherever it was.
 */
async function deriveThumb(sources: PhotoByteSources, ref: PhotoRef, original: Blob): Promise<Blob> {
  let bytes: ArrayBuffer | null;
  try {
    bytes = await sources.makeThumb(original);
  } catch {
    bytes = null;
  }
  if (!bytes || bytes.byteLength === 0) return original;
  await keep(() => sources.putThumb(ref.id, ref.surveyId, bytes));
  return thumbBlobOf(bytes);
}

export async function resolveThumbBlob(
  sources: PhotoByteSources,
  ref: PhotoRef,
): Promise<Blob | null> {
  const localThumb = await read(() => sources.getThumb(ref.id));
  if (localThumb && localThumb.byteLength > 0) return thumbBlobOf(localThumb);

  // Before the original, because the office's thumb is tens of kilobytes and
  // the original on the phone is a whole picture to decode.
  const held = isHeldByOffice(ref);
  if (held) {
    const remoteThumb = await ask(() => sources.fetchRemoteThumb(ref.surveyId, ref.id));
    // An empty body is not a picture; the original paths below still run.
    if (remoteThumb && remoteThumb.size > 0) {
      await keep(async () => sources.putThumb(ref.id, ref.surveyId, await remoteThumb.arrayBuffer()));
      return remoteThumb;
    }
  }

  const localOriginal = await read(() => sources.getLocal(ref.id));
  if (localOriginal) return deriveThumb(sources, ref, localOriginal);
  if (!held) return null;

  const remoteOriginal = await ask(() => sources.fetchRemote(ref.surveyId, ref.id));
  if (!remoteOriginal) return null;
  // Kept as the original it is, so the viewer's own resolution is local next time.
  await keep(() => sources.putLocal(ref.id, ref.surveyId, remoteOriginal));
  return deriveThumb(sources, ref, remoteOriginal);
}

export interface ThumbnailSetPlan {
  /** The URLs still wanted — the same object as `held` when nothing left. */
  keep: Record<string, string>;
  /** The URLs of the ids that left the set; revoke them, they point at nothing anyone draws. */
  revoke: string[];
  /** The ids that need a resolution: wanted, no URL yet, none in flight. */
  resolve: string[];
}

/**
 * What `useThumbnails` (PhotoCapture.tsx) does when the set of photos changes
 * (final review's fix wave, D-040 postscript). The hook used to revoke every
 * URL it had minted and then keep the revoked strings in state for the ids
 * that survived, and re-resolve every card on every capture; this is the set
 * arithmetic that replaces it — only what LEFT is revoked, only what is NEW
 * (and not already being resolved) is asked for, and the rest is kept as it
 * was. `held` is returned as `keep` untouched when nothing left, so the hook
 * can tell "no render" from "a smaller map".
 */
export function planThumbnailSet(
  held: Readonly<Record<string, string>>,
  wanted: readonly string[],
  inFlight: ReadonlySet<string>,
): ThumbnailSetPlan {
  const wantedSet = new Set(wanted);
  const revoke: string[] = [];
  for (const [id, url] of Object.entries(held)) {
    if (!wantedSet.has(id)) revoke.push(url);
  }
  const keep =
    revoke.length === 0
      ? (held as Record<string, string>)
      : Object.fromEntries(Object.entries(held).filter(([id]) => wantedSet.has(id)));
  const resolve = wanted.filter((id) => !(id in held) && !inFlight.has(id));
  return { keep, revoke, resolve };
}

/**
 * Every ref's thumbnail, `limit` at a time, each handed to `onThumb` the
 * moment it resolves — in the order they finish, not the order they were
 * asked. A ref with no bytes anywhere hands over nothing. Resolves once
 * every ref has been tried. This is the shape `useThumbnails` publishes in
 * (round 8.1, task 4): before it, one map after the slowest card, so a hand
 * whose bytes were all lost stayed `▣` until the last fetch was in.
 */
export async function resolveThumbsAsTheyLand(
  refs: readonly PhotoRef[],
  sources: PhotoByteSources,
  limit: number,
  onThumb: (ref: PhotoRef, blob: Blob) => void,
): Promise<void> {
  await mapWithLimit(refs, limit, async (ref) => {
    const blob = await resolveThumbBlob(sources, ref);
    if (blob) onThumb(ref, blob);
  });
}

/**
 * `Promise.all` with a ceiling on how many `fn` calls run at once.
 *
 * Final review of round 8 (D-039 postscript): "Mema's House" carries 120
 * photos, and the hook in PhotoCapture.tsx resolved every card in one
 * `Promise.all` — on a phone that had lost its bytes that was 120 concurrent
 * `GET /api/photos/…` in one render. The results come back in the items'
 * order, whatever order they finished in. One rejection rejects the whole
 * call, as `Promise.all` does, but it stalls nothing: the queue keeps
 * draining so every item still runs, and a caller whose `fn` never throws
 * (the hook's does not) never sees the difference.
 */
export async function mapWithLimit<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array<R>(items.length);
  let next = 0;
  // An object, not a `let`, because the workers assign it inside a closure
  // and TypeScript would otherwise narrow it to `null` at the throw below.
  const failure: { hit: boolean; error: unknown } = { hit: false, error: undefined };
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        results[index] = await fn(items[index] as T, index);
      } catch (error) {
        if (!failure.hit) {
          failure.hit = true;
          failure.error = error;
        }
      }
    }
  };
  const width = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: width }, () => worker()));
  if (failure.hit) throw failure.error;
  return results;
}
